import axios from 'axios'
import { useAuth } from '@clerk/clerk-react'
import { API_URL } from '@/constants'
import {GameGlassUser} from "@/hooks/useCurrentUser";
import {useMemo} from "react";

export interface Collection {
  id: number,
  name: string,
  cover_url?: string,
  artwork_url?: string,
  shard_count: number,
  company_name?: string
}

export interface Shard {
  id: number
  name: string
  created_at: string
  updated_at: string
  gameglass: boolean,
  premium: boolean,
  glasspass: boolean,
  community: boolean,
  user: {
    id: number,
    username: string
  },
  image?: string
  screenshot_thumbnail?: string
  screenshot?: string
  collection?: Collection
  public: boolean
  height: number
  width: number
  devices?: string[],
  variants: [],
  imported: boolean,
  editing_allowed: boolean,
  subscription: boolean,
  subscribeable: boolean,
  description: string
  source_shard?: {
    id: number
    username: string
    user_id: number
  }
}

export interface ResultsResponse<T> extends CountResponse {
  results: T[]
}

export interface CountResponse {
  count: number
}

export const useAPI = () => {
  const { getToken } = useAuth()

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
      timeout: 20000,
    });
    instance.interceptors.request.use(
        async config => {
          const token = await getToken()
          if (token && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`
          }

          return config
        },
        error => {
          return Promise.reject(error)
        }
    )
    return instance
  }, [getToken])

  return {
    getUserInfo: async () => await api.get<GameGlassUser>('users/@me'),
    getUserCollections: async (params: Record<string, number | string | boolean> = {}) => await api.get<ResultsResponse<Collection>>('/users/@me/collections', {
      params
    }),
    getUserShardCount: async (params: Record<string, number | string | boolean> = {}) => await api.get<CountResponse>('/users/@me/shards/count', {
      params
    }),
    getUserShards: async (params: Record<string, number | string | boolean> = {}) => await api.get<ResultsResponse<Shard>>('/users/@me/shards', {
      params
    }),
  }
}
